//////////////////////////////////////////////////
// Home Screen
//////////////////////////////////////////////////

import Lobby from './Lobby';
import OS from '../tablet/OS';
import IO from '../tablet/IO';
import Project from '../editor/ui/Project';
import Localization from '../utils/Localization';
import ScratchAudio from '../utils/ScratchAudio';
import Vector from '../geom/Vector';
import {gn, newHTML, isTablet} from '../utils/lib';
import StarterTemplates from './StarterTemplates';

let frame;
let scrollvalue;
let version;
let timeoutEvent;
let performingAction = false;
let refreshRetryCount = 0;
const MAX_REFRESH_RETRIES = 5;
const REFRESH_RETRY_DELAY_MS = 500;

// UI readiness retry for initial render race (Issue #608)
let uiRetryCount = 0;
const MAX_UI_RETRIES = 10;
const UI_RETRY_DELAY_MS = 100;

// Deterministic initial render state
let initialRenderStarted = false;
let initialRenderDone = false;
let initialRenderPromise = null;
// Render stability guards to prevent flashing/jitter from rapid events
let _renderInProgress = false;
let _needsRerender = false;
let _renderScheduledTimer = null;
let _renderLastRun = 0;
const RENDER_COOLDOWN_MS = 200;
// Freeze lobby updates after first stable render; allow exactly one forced refresh after editor return
let _freezeLobbyRefresh = false;
let _allowOneForcedRefresh = false;

// Hidden staging area for flash-free rendering (Issue #638)
let _hiddenStaging = null;  // Hidden container where all rendering happens
let _renderDebounceTimer = null;
let _lastRenderTime = 0;
let _isRendering = false;  // Lock to prevent concurrent renders
let suppressEventDrivenRenders = false;  // Suppress renders after deterministic init
// Issue #638: Reduced to 100ms for instant feel with DocumentFragment optimization
// - Previous 300ms caused visible 1-2s delay
// - DocumentFragment swap is instant (single paint), can afford shorter debounce
// - 100ms still allows rapid events to settle without visible lag
const RENDER_SETTLE_MS = 100;
const RENDER_SUPPRESSION_MS = 1000;  // Suppress event-driven renders for 1s after init

function getActiveCloudProfileId () {
    try {
        return (
            localStorage.getItem('scratchjr_profile_id') ||
            sessionStorage.getItem('scratchjr_profile_id') ||
            (window.cloudSyncService && window.cloudSyncService.getStatus && window.cloudSyncService.getStatus().profileId) ||
            'default'
        );
    } catch (_) {
        return 'default';
    }
}

function projectMatchesActiveProfile (project) {
    if (!project) return false;

    const metadata = project.metadata || {};
    const isStarter = project.starterFlag === '1' || metadata.starter === '1';
    if (isStarter) return true;

    const projectProfileId = String(project.profileId || metadata.profileId || '').trim();
    const activeProfileId = String(getActiveCloudProfileId() || 'default').trim();

    if (!projectProfileId) {
        return activeProfileId === 'default';
    }

    if (activeProfileId === 'default') {
        return projectProfileId === 'default' || projectProfileId === 'default-profile';
    }

    return projectProfileId === activeProfileId;
}

export default class Home {
    static init () {
        console.log('🏠 Home.init() called - initializing lobby');
        // Track whether we've performed a one-time cache refresh
        if (typeof Home._didInitialCacheRefresh === 'undefined') {
            Home._didInitialCacheRefresh = false;
        }
        // Ensure container exists synchronously to avoid empty-first paint
        version = Lobby.version;
        frame = gn('htmlcontents');
        if (!gn('scrollarea')) {
            var inner = newHTML('div', 'inner', frame);
            var div = newHTML('div', 'scrollarea', inner);
            div.setAttribute('id', 'scrollarea');
            // Try to install gift tile early
            try { if (typeof window.installGiftTriggerTile === 'function') { window.installGiftTriggerTile(); } } catch (_) {}
        } else {
            console.log('ℹ️ Home.init(): scrollarea already present');
        }
        frame.ontouchstart = Home.handleTouchStart;
        frame.ontouchend = Home.handleTouchEnd;
        frame.onmousedown = Home.handleTouchStart;
        frame.onmouseup = Home.handleTouchEnd;

        // Kick off deterministic initial render (sequential, ordered)
        try { if (!initialRenderStarted && !initialRenderDone) { (async () => { await Home.renderDeterministic(); })(); } } catch(_) {}

        // Update lobby when templates are seeded asynchronously
        try {
            window.addEventListener('templates:seeded', function () {
                try { Home.displayYourProjects(); } catch (_) {}
            });
        } catch (_) {}

        // Refresh project list automatically after database cache refresh
        try {
            if (!Home._eventsHooked) {
                window.addEventListener('dbmodule:cache-refreshed', function(){
                    try { Home.displayYourProjects(); } catch (_) {}
                });
                window.addEventListener('dbmodule:project-inserted', function(){
                    try { Home.displayYourProjects(); } catch (_) {}
                });
                window.addEventListener('dbmodule:project-updated', function(){
                    try { Home.displayYourProjects(); } catch (_) {}
                });
                window.addEventListener('auth:profile-changed', function(){
                    try { Home.displayYourProjects(); } catch (_) {}
                });
                window.addEventListener('auth:logout', function(){
                    try { Home.displayYourProjects(); } catch (_) {}
                });
                Home._eventsHooked = true;
            }
        } catch (_) {}

        // Gate initial render on cache readiness when coming directly from index
        try {
            let fromIndex = false;
            try { fromIndex = sessionStorage.getItem('sjr:from-index') === '1'; if (fromIndex) sessionStorage.removeItem('sjr:from-index'); } catch(_) {}
            const db = (typeof window !== 'undefined') ? window.dbModule : null;
            const cacheReady = !!(db && db.cacheReady && db.projectCache && db.projectCache.size >= 0);
            const hasWarmSnap = (function(){ try { return !!sessionStorage.getItem('sjr:warm-projects'); } catch(_) { return false; }})();
            if (fromIndex && !hasWarmSnap && !cacheReady) {
                console.log('⏳ Home.init(): fast trip from index and cache not ready; deferring initial render until cache is ready');
                const render = function(){ try { Home.displayYourProjects(); } catch(_) {} };
                let done = false;
                function once(){ if (done) return; done = true; render(); }
                try { window.addEventListener('dbmodule:cache-refreshed', once, { once: true }); } catch(_) {}
                try { window.addEventListener('dbmodule:project-inserted', once, { once: true }); } catch(_) {}
                // Safety timeout
                setTimeout(once, 1800);
                return;
            }
        } catch(_) {}

        // Do not trigger an additional immediate render to avoid racey double-paints.
        // Subsequent updates will use event-driven displayYourProjects.

        // Deterministic starter seeding (no legacy DB queries).
        (async () => {
            try {
                if (window.dbModuleInitPromise) { await window.dbModuleInitPromise; }
                let defaultsSeeded = false;
                try { defaultsSeeded = (localStorage.getItem('sjr:defaults-seeded') === '1') || !!localStorage.getItem('scratchjr_file_starter_templates_loaded_v1'); } catch(_) {}
                if (!defaultsSeeded && window.dbModule && typeof window.dbModule.ensureDefaultProjects === 'function') {
                    await window.dbModule.ensureDefaultProjects();
                    try { localStorage.setItem('sjr:defaults-seeded', '1'); } catch(_) {}
                    console.log('✅ [Home] Seeded templates via DatabaseModule');
                }
            } catch (e) {
                console.warn('⚠️ [Home] Starter seeding skipped (db unavailable):', e && e.message);
            } finally {
                try { Home.displayYourProjects(); } catch (_) {}
            }
        })();
    }

    ////////////////////////////
    // Home Screen
    ////////////////////////////

    static emptyProjectThumbnail (parent) {
        var tb = newHTML('div', 'projectthumb', parent);
        newHTML('div', 'aproject empty', tb);
        tb.id = 'newproject';
    }

    //////////////////////////
    // Events
    //////////////////////////

    static handleTouchStart (e) {
        // On my android tablet, when touching a project,
        // the tablet triggers the mousedown event
        // after touchstart event about 600ms
        // --Donald
        try {
            // Ignore interactions on the gift import tile to avoid treating it as a project
            let t = e && e.target;
            while (t && t !== frame && t !== document && t.nodeType === 1) {
                if (t.id === 'gifttrigger') {
                    if (e && typeof e.preventDefault === 'function') e.preventDefault();
                    return; // Do not set actionTarget or performingAction for gift tile
                }
                t = t.parentNode;
            }
        } catch (_) {}
        if (performingAction) {
            return;
        }
        performingAction = true;
        setTimeout(function () {
            performingAction = false;
        }, 1000);
        Home.dragging = false;
        Home.holding = false;
        // if ((t.nodeName == "INPUT") || (t.nodeName == "FORM")) return;
        var mytarget = Home.getMouseTarget(e);
        if ((mytarget != Home.actionTarget) && Home.actionTarget && (Home.actionTarget.childElementCount > 2)) {
            Home.actionTarget.childNodes[Home.actionTarget.childElementCount - 1].style.visibility = 'hidden';
        }
        Home.actionTarget = mytarget;
        Home.initialPt = Events.getTargetPoint(e);
        if (Home.actionTarget) {
            holdit(Home.actionTarget);
        }
        function holdit () {
            frame.ontouchmove = Home.handleMove;
            frame.onmousemove = Home.handleMove;
            var repeat = function () {
                if (Home.actionTarget && (Home.actionTarget.childElementCount > 2)) {
                    Home.actionTarget.childNodes[Home.actionTarget.childElementCount - 1].style.visibility = 'visible';

                    Home.holding = true;
                }
            };
            timeoutEvent = setTimeout(repeat, 500);
        }
        Home.scrolltop = document.body.scrollTop;
    }

    static handleMove (e) {
        var pt = Events.getTargetPoint(e);
        var delta = Vector.diff(pt, Home.initialPt);
        if (!Home.dragging && (Vector.len(delta) > 20)) {
            Home.dragging = true;
        }
        if (!Home.dragging) {
            return;
        }
        if (timeoutEvent) {
            clearTimeout(timeoutEvent);
        }
        timeoutEvent = undefined;
    }

    static getMouseTarget (e) {
        var t = e.target;
        if (t == frame) {
            return null;
        }
        if (t.parentNode && !t.parentNode.tagName) {
            return null;
        }
        while (t.parentNode && (t.parentNode != frame) && (t.parentNode.getAttribute('class') != 'scrollarea')) {
            t = t.parentNode;
        }
        return (!t.parentNode || (t.parentNode == frame)) ? null : t;
    }

    static handleTouchEnd (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.touches && (e.touches.length > 1)) {
            return;
        }
        frame.ontouchmove = undefined;
        frame.onmousemove = undefined;
        if (timeoutEvent) {
            clearTimeout(timeoutEvent);
        }
        timeoutEvent = undefined;
        if (Home.dragging) {
            return;
        }
        Home.performAction(e);
    }

    static performAction (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!Home.actionTarget) {
            return;
        }
        if (Home.holding) {
            return;
        }
        var md5 = Home.actionTarget.id;
        var isTemplate = Home.actionTarget.type === 'templatethumb';

        switch (Home.getAction(e)) {
        case 'project':
            ScratchAudio.sndFX('keydown.wav');
            if (md5 && (md5 == 'newproject')) {
                Home.createNewProject();
            } else if (md5 && (isTemplate || (Home.actionTarget && Home.actionTarget.getAttribute && Home.actionTarget.getAttribute('data-starter') === '1'))) {
                // Template clicked - create a copy and open it
                console.log('📋 [Home] Template clicked, creating copy:', md5);
                OS.analyticsEvent('lobby', 'template_copied', md5);
                StarterTemplates.copyTemplate(md5, (newProjectId) => {
                    if (newProjectId) {
                        console.log('✅ [Home] Template copied, opening new project:', newProjectId);
                        Home.gotoEditor(newProjectId);
                    } else {
                        console.error('❌ [Home] Failed to copy template');
                    }
                });
            } else if (md5) {
                // Regular user project - open directly
                OS.setfile('homescroll.sjr', gn('wrapc').scrollTop, function () {
                    doNext(md5);
                });
            }
            break;
        case 'delete':
            ScratchAudio.sndFX('cut.wav');

            // Lift render suppression to ensure deletion shows immediately
            if (suppressEventDrivenRenders) {
                console.log('🗑️ Deletion detected - lifting render suppression for immediate update');
                suppressEventDrivenRenders = false;
            }

            // Fix Issue #446: Comprehensive cleanup of ALL project-related files
            var projectId = Home.actionTarget.id;

            // Step 1: Remove main project thumbnail
            Project.thumbnailUnique(Home.actionTarget.thumb, Home.actionTarget.id, function (isUnique) {
                if (isUnique) {
                    console.log('🗑️ Removing main project thumbnail:', Home.actionTarget.thumb);
                    OS.remove(Home.actionTarget.thumb, OS.trace);
                }
            });

            // Step 2: Clean up ALL associated localStorage files
            // This catches orphaned thumbnails, media, and assets that accumulate over time
            Home.cleanupProjectFiles(projectId);

            // Step 3: Delete the project record from database
            IO.deleteobject(OS.database, projectId, Home.removeProjThumb);
            break;
        default:
            if (Home.actionTarget && (Home.actionTarget.childElementCount > 2)) {
                Home.actionTarget.childNodes[Home.actionTarget.childElementCount - 1].style.visibility = 'hidden';
            }
            break;
        }
        function doNext () {
            OS.analyticsEvent('lobby', 'existing_project_edited');
            window.location.href = 'editor.html?pmd5=' + md5 + '&mode=edit';
        }
    }

    static createNewProject () {
        // Guard to prevent duplicate creations from double events
        if (Home._creatingProject) { return; }
        Home._creatingProject = true;
        try { OS.analyticsEvent('lobby', 'project_created'); } catch (_) {}

        // Fast path: jump straight to editor with a new project ID.
        // Do NOT insert into DB from the lobby — this avoids a temporary blank tile.
        try {
            // Generate a new project id (proj_<timestamp>_<rand>)
            var ts = Date.now();
            var rand = Math.random().toString(36).substr(2, 8);
            var newId = 'proj_' + ts + '_' + rand;

            // Optionally hint the editor to refresh lobby upon return
            try { sessionStorage.setItem('sjr:last-saved-project', newId); } catch (_) {}

            // Small delay to avoid rapid re-entry, then navigate
            setTimeout(function(){ Home._creatingProject = false; }, 250);
            Home.gotoEditor(newId);
            return;
        } catch (e) {
            // Fallback (should not happen): use legacy path
        }

        // Legacy fallback: if ID generation fails for some reason, use previous flow
        var obj = {};
        var base = Home.getNextName(Localization.localize('NEW_PROJECT_PREFIX'));
        var finalize = function(finalName){
            obj.name = finalName || base;
            obj.version = version;
            obj.mtime = (new Date()).getTime().toString();
            IO.createProject(obj, function(id){
                setTimeout(function(){ Home._creatingProject = false; }, 500);
                Home.gotoEditor(id);
            });
        };
        try {
            if (typeof IO.uniqueProjectName === 'function') {
                IO.uniqueProjectName({ name: base }, function(res){ finalize(res && res.name ? res.name : base); }, true);
                return;
            }
        } catch (_) {}
        finalize(base);
    }

    static gotoEditor (md5) {
        OS.setfile('homescroll.sjr', gn('wrapc').scrollTop, function () {
            doNext(md5);
        });
        function doNext (md5) {
            window.location.href = 'editor.html?pmd5=' + md5 + '&mode=edit';
        }
    }

    // Project names are given by reading the DOM elements of existing projects...
    static getNextName (name) {
        var pn = [];
        var div = gn('scrollarea');
        if (!div) { return name + ' 1'; }
        for (var i = 0; i < div.childElementCount; i++) {
            var node = div.childNodes[i];
            // Skip special tiles that are not real projects
            if (!node || node.id === 'newproject' || node.id === 'gifttrigger') { continue; }
            // Prefer robust query to avoid DOM structure assumptions
            var label = (node.querySelector) ? node.querySelector('.projecttitle h4') : null;
            if (label && typeof label.textContent === 'string') {
                pn.push(label.textContent);
            } else {
                // Fallback to legacy child traversal if available
                try {
                    var maybe = node.childNodes && node.childNodes[1] && node.childNodes[1].childNodes && node.childNodes[1].childNodes[0];
                    if (maybe && typeof maybe.textContent === 'string') { pn.push(maybe.textContent); }
                } catch (_) {}
            }
        }
        var n = 1;
        while (pn.indexOf(name + ' ' + n) > -1) { n++; }
        return name + ' ' + n;
    }

    static removeProjThumb () {
        if (Home.actionTarget && Home.actionTarget.parentNode) {
            Home.actionTarget.parentNode.removeChild(Home.actionTarget);
        }
        Home.actionTarget = undefined;
    }

    /**
     * Fix Issue #446: Clean up ALL localStorage files associated with a project
     * This removes orphaned thumbnails, media, and assets that accumulate over time
     *
     * @param {string} projectId - The project ID to clean up
     */
    static cleanupProjectFiles(projectId) {
        if (typeof localStorage === 'undefined') {
            return;
        }

        console.log('🧹 Cleaning up files for project:', projectId);

        const keysToRemove = [];
        const prefixes = [
            `scratchjr_file_${projectId}`,      // Old files
            `scratchjr_media_${projectId}`,     // Media files
            `scratchjr_asset_${projectId}`,     // Asset/thumbnail files
            `scratchjr_medianame_${projectId}`, // Media metadata
            `projects_${projectId}`             // Project metadata
        ];

        // Scan all localStorage keys
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
                // Check if key matches any project-related prefix
                for (const prefix of prefixes) {
                    if (key.startsWith(prefix)) {
                        keysToRemove.push(key);
                        break;
                    }
                }
            }
        }

        // Remove all collected keys
        keysToRemove.forEach(key => {
            console.log('  🗑️ Removing:', key);
            localStorage.removeItem(key);
        });

        console.log(`✅ Cleaned up ${keysToRemove.length} orphaned files for project ${projectId}`);
    }

    static getAction (e) {
        if (!Home.actionTarget) {
            return 'none';
        }
        var shown = (Home.actionTarget.childElementCount > 2) ?
            Home.actionTarget.childNodes[Home.actionTarget.childElementCount - 1].style.visibility == 'visible' :
            false;
        if (e && shown) {
            var t;
            if (window.event) {
                t = window.event.srcElement;
            } else {
                t = e.target;
            }
            if (t.getAttribute('class') == 'closex') {
                return 'delete';
            }
        }
        return 'project';
    }

    //////////////////////////
    // Gather projects
    //////////////////////////

    static displayYourProjects () {
        // Debounce/coalesce rapid render requests
        const nowTs = Date.now();
        if (_renderInProgress) { _needsRerender = true; return; }
        if ((nowTs - _renderLastRun) < RENDER_COOLDOWN_MS) {
            if (_renderScheduledTimer) { try { clearTimeout(_renderScheduledTimer); } catch(_) {} }
            _renderScheduledTimer = setTimeout(() => { _renderScheduledTimer = null; Home.displayYourProjects(); }, RENDER_COOLDOWN_MS);
            return;
        }
        _renderInProgress = true;
        // Guard: only render project list on the actual home (lobby) page
        // Prevents project grid from appearing on settings/help/guide pages (Issue: settings overlap)
        try {
            const frameEl = gn('htmlcontents');
            const top = gn('topsection');
            const isHome = (frameEl && typeof frameEl.className === 'string' && /(^|\s)home(\s|$)/.test(frameEl.className))
                        || (top && typeof top.className === 'string' && /(^|\s)home(\s|$)/.test(top.className));
            if (!isHome) {
                // Not in home context; skip rendering
                _renderInProgress = false;
                return;
            }
        } catch (_) {}
        // Avoid racing with deterministic initial render
        if (initialRenderStarted && !initialRenderDone) {
            try { setTimeout(() => { try { Home.displayYourProjects(); } catch(_){} }, 200); } catch(_) {}
            _renderInProgress = false;
            return;
        }
        // Suppress event-driven renders immediately after deterministic init
        // This prevents double-flash where renderDeterministic() shows projects,
        // then event-driven displayYourProjects() swaps identical content
        if (suppressEventDrivenRenders) {
            console.log('⏭️ Suppressing event-driven render - deterministic init recently completed');
            return;
        }
        // CRITICAL FIX (Issue #608): Await dbModule initialization before displaying projects
        // Root cause: displayYourProjects() was called before dbModule finished loading
        console.log('📋 displayYourProjects() called');
        try {
            console.log('🔍 Current window.dbModule:', !!window.dbModule, window.dbModule && {
                cacheReady: window.dbModule.cacheReady,
                cacheSize: (window.dbModule.projectCache && window.dbModule.projectCache.size) || 0
            });
        } catch (_) { console.log('🔍 Current window.dbModule: <unavailable>'); }
        try { console.log('🔍 Current window.dbModuleInitPromise:', typeof window.dbModuleInitPromise); } catch (_) {}

        // Detect force refresh (URL or sessionStorage) and last-saved ID for diagnostics
        let forceRefresh = false;
        let expectedProjectId = null;
        try {
            const url = new URL(window.location.href);
            if ((url.searchParams.get('from') || '').toLowerCase() === 'editor' || url.searchParams.get('refresh') === '1') {
                console.log('🔄 Detected navigation from editor via URL param');
                forceRefresh = true;
            }
        } catch (_) {}
        try {
            const token = sessionStorage.getItem('sjr:refresh-lobby');
            const forceToken = sessionStorage.getItem('sjr:force-cache-refresh');
            if (token === '1' || forceToken === 'true') {
                console.log('🔄 Detected refresh token in sessionStorage');
                forceRefresh = true;
                sessionStorage.removeItem('sjr:refresh-lobby');
                sessionStorage.removeItem('sjr:force-cache-refresh');
            }
            expectedProjectId = sessionStorage.getItem('sjr:last-saved-project');
        } catch (_) {}
        console.log('🔍 Force refresh flag:', forceRefresh);
        console.log('🔍 Expected project ID:', expectedProjectId);

        // If we are frozen after initial render, skip unless a force refresh is requested (editor → home)
        if (_freezeLobbyRefresh) {
            try {
                // Recompute force flag here from the same logic below (keep in sync)
                let force = false;
                try {
                    const url = new URL(window.location.href);
                    if ((url.searchParams.get('from') || '').toLowerCase() === 'editor' || url.searchParams.get('refresh') === '1') {
                        force = true;
                    }
                } catch (_) {}
                try {
                    const token = sessionStorage.getItem('sjr:refresh-lobby');
                    const forceToken = sessionStorage.getItem('sjr:force-cache-refresh');
                    if (token === '1' || forceToken === 'true') { force = true; }
                } catch (_) {}
                if (!force) {
                    _renderInProgress = false;
                    return;
                }
                if (force && !_allowOneForcedRefresh) {
                    _renderInProgress = false;
                    return;
                }
            } catch (_) {}
        }

        // Check if initialization promise exists
        if (window.dbModuleInitPromise) {
            console.log('⏳ Waiting for dbModule initialization...');
            window.dbModuleInitPromise.then(async (dbModule) => {
                try {
                    if (dbModule) {
                        if (typeof dbModule.waitForWrites === 'function') {
                            console.log('⏳ Waiting for pending writes to drain...');
                            try { await dbModule.waitForWrites(); } catch (_) {}
                        }
                        if (forceRefresh && typeof dbModule.refreshCache === 'function') {
                            console.log('♻️ Refreshing cache via dbModule.refreshCache()');
                            try { await dbModule.refreshCache(); } catch (e) { console.warn('⚠️ refreshCache failed:', e && e.message || e); }
                        }
                        console.log(`📦 Cache status before render: ready=${dbModule.cacheReady} size=${(dbModule.projectCache && dbModule.projectCache.size) || 0}`);
                        loadProjects();
                    } else {
                        console.error('❌ dbModule unresolved/null, proceeding without cache');
                        loadProjects();
                    }
                } catch (err) {
                    console.error('❌ dbModule initialization error:', err);
                    loadProjects();
                }
            }).catch(err => {
                console.error('❌ dbModule initialization promise rejected:', err);
                loadProjects();
            });
        } else {
            // Fallback: dbModuleInitPromise doesn't exist (shouldn't happen)
            console.warn('⚠️ dbModuleInitPromise not found, proceeding with direct load');
            loadProjects();
        }

        function loadProjects() {
            console.log('🔍 loadProjects: Getting scroll state...');
            try {
                if (OS && typeof OS.getfile === 'function') {
                    OS.getfile('homescroll.sjr', gotScrollsState);
                } else {
                    console.warn('⚠️ OS.getfile unavailable; defaulting scroll state');
                    gotScrollsState(btoa('0'));
                }
            } catch (e) {
                console.warn('⚠️ OS.getfile error; defaulting scroll state:', e && e.message);
                gotScrollsState(btoa('0'));
            }
            function gotScrollsState (str) {
                console.log('📜 Got scroll state, querying database...');
                var num = 0;
                try { num = Number(atob(str)); } catch(_) { num = 0; }
                scrollvalue = (num.toString() == 'NaN') ? 0 : num;
                // Prefer synchronous cache from DatabaseModule when available
                try {
                    if (window.dbModule && window.dbModule.projectCache) {
                        console.log('📦 Using dbModule.projectCache as source of truth');
                        const renderFromCache = () => {
                            const all = Array.from(window.dbModule.projectCache.values());
                            console.log('🔍 [Home] Filtering projects - total in cache:', all.length);
                            console.log('🔍 [Home] Expected version:', version);

                            const afterDeletedFilter = all.filter(function(p){
                                // Fix Issue #681: Treat undefined as 'NO' (not deleted)
                                // Cloud sync may return deleted: undefined for projects
                                var del = (p.metadata && p.metadata.deleted !== undefined)
                                    ? p.metadata.deleted
                                    : (p.deleted !== undefined ? p.deleted : 'NO');
                                const keep = del === 'NO';
                                if (!keep) console.log('  ❌ Filtered out (deleted):', p.id, 'deleted=', del);
                                return keep;
                            });
                            console.log('🔍 [Home] After deleted filter:', afterDeletedFilter.length);

                            // Version filter removed (Issue #681) - show all projects regardless of version
                            // This enables cloud sync to work across different version formats
                            console.log('🔍 [Home] Version filter: DISABLED (showing all versions)');

                            const projects = afterDeletedFilter.filter(function(p){
                                var gal = (p.metadata && p.metadata.gallery) ? p.metadata.gallery : (p.gallery || null);
                                const keep = gal === null || gal === undefined;
                                if (!keep) console.log('  ❌ Filtered out (gallery):', p.id, 'gallery=', gal);
                                return keep;
                            })
                            .filter(function(p){
                                const keep = projectMatchesActiveProfile(p);
                                if (!keep) console.log('  ❌ Filtered out (profile):', p.id, 'profileId=', p && p.profileId, 'activeProfileId=', getActiveCloudProfileId());
                                return keep;
                            })
                            .sort(function(a, b){
                                // Use same sort logic as renderDeterministic: mtime with fallback
                                var am = (a.mtime != null ? parseInt(a.mtime, 10) : null);
                                var bm = (b.mtime != null ? parseInt(b.mtime, 10) : null);
                                if (isNaN(am)) am = (a.lastModified || a.createdAt || 0);
                                if (isNaN(bm)) bm = (b.lastModified || b.createdAt || 0);
                                if ((bm||0) !== (am||0)) return (bm||0) - (am||0);
                                // Tie-breaker by id for stability
                                try { return String(a.id).localeCompare(String(b.id)); } catch (_) { return 0; }
                            })
                            .map(function(p){ return ({
                                id: p.id,
                                name: p.name,
                                thumbnail: (typeof p.thumbnail === 'string' ? p.thumbnail : JSON.stringify(p.thumbnail || {})),
                                isgift: (p.metadata && p.metadata.isgift) || p.isgift || '0'
                            }); });
                            console.log('🔍 [Home] After gallery filter (final):', projects.length);
                            try {
                                console.log('📊 Render from cache - total:', all.length, 'filtered:', projects.length, 'ids:', projects.map(function(p){return p.id;}));
                            } catch (_) {}
                            const serialized = JSON.stringify(projects);
                            return Home.displayProjects(serialized);
                        };
                        try {
                            if (typeof window.dbModule.waitForWrites === 'function') {
                                return window.dbModule.waitForWrites().then(async () => {
                                    try {
                                        if (!Home._didInitialCacheRefresh && typeof window.dbModule.refreshCache === 'function') {
                                            await window.dbModule.refreshCache();
                                            Home._didInitialCacheRefresh = true;
                                        }
                                    } catch (_) {}
                                    return renderFromCache();
                                });
                            }
                        } catch (_) {}
                        // Best effort refresh before first paint
                        try {
                            if (!Home._didInitialCacheRefresh && typeof window.dbModule.refreshCache === 'function') {
                                window.dbModule.refreshCache().catch(()=>{});
                                Home._didInitialCacheRefresh = true;
                            }
                        } catch (_) {}
                        return renderFromCache();
                    }
                } catch (e) {
                    console.warn('⚠️ Failed to use cache directly:', e.message);
                }

                // No legacy fallback: wait for cache and retry
                console.log('⏳ Cache not available; retrying displayYourProjects in 250ms');
                setTimeout(function(){ try { Home.displayYourProjects(); } catch(_){} }, 250);
            }
        }
    }

    static displayProjects (str) {
        console.log('🎨 displayProjects() called - rendering to HIDDEN staging area');

        // Race condition guard: Skip if already rendering (Claude review issue #2)
        if (_isRendering) {
            console.log('⏭️ Skipping concurrent render - already in progress');
            return;
        }
        _isRendering = true;

        try {
            // Guard: ensure we are on the home page before mutating the grid
            try {
                const frameEl = gn('htmlcontents');
                const top = gn('topsection');
                const isHome = (frameEl && typeof frameEl.className === 'string' && /(^|\s)home(\s|$)/.test(frameEl.className))
                            || (top && typeof top.className === 'string' && /(^|\s)home(\s|$)/.test(top.className));
                if (!isHome) { return; }
            } catch(e) {
                console.warn('⚠️ Home page detection failed:', e.message);
            }

            let parsed;
            try { parsed = JSON.parse(str); } catch (e) {
                console.warn('⚠️ Failed to parse project data:', e.message);
                parsed = [];
            }
            const data = Array.isArray(parsed) ? parsed : (parsed && Array.isArray(parsed.rows) ? parsed.rows : []);
            try { if (data.length > 0) { console.log(`📊 Parsed ${data.length} projects from query result`); } } catch (_) {}

            // Create hidden staging container if needed (NEVER VISIBLE)
            if (!_hiddenStaging) {
                _hiddenStaging = document.createElement('div');
                _hiddenStaging.id = 'scrollarea-hidden';
                _hiddenStaging.className = 'scrollarea';
                _hiddenStaging.style.position = 'absolute';
                _hiddenStaging.style.left = '-9999px';  // Off-screen
                _hiddenStaging.style.visibility = 'hidden';  // Extra insurance
                document.body.appendChild(_hiddenStaging);
                console.log('📦 Created HIDDEN staging container for flash-free rendering');
            }

            // Render to HIDDEN staging area (user never sees this)
            var div = _hiddenStaging;

            // Clear hidden staging area for fresh render
            try {
                while (div.firstChild) div.removeChild(div.firstChild);
            } catch (e) {
                console.warn('⚠️ Failed to clear staging container:', e.message);
            }

            Home.emptyProjectThumbnail(div);
            // Ensure gift tile right after new project tile before any other items
            try { if (typeof window.installGiftTriggerTile === 'function') { window.installGiftTriggerTile(); } } catch (_) {}

            // First, add template projects (prefer dbModule cache if ready)
            const renderAfterTemplates = (templates) => {
                try {
                    // De-duplicate by name to avoid duplicates when multiple seeders run
                    const seen = new Set();
                    const uniq = [];
                    for (var j = 0; j < (templates || []).length; j++) {
                        try {
                            var tdata = IO.parseProjectData(templates[j]);
                            var nm = tdata && tdata.name;
                            if (nm && !seen.has(nm)) { seen.add(nm); uniq.push(templates[j]); }
                        } catch(_) { /* ignore */ }
                    }
                    console.log('📦 [Home] Displaying', uniq.length, 'starter templates');
                    for (var k = 0; k < uniq.length; k++) { Home.addTemplateLink(div, uniq[k]); }
                } catch (_) { /* fallback just in case */
                    console.log('📦 [Home] Displaying', (templates||[]).length, 'starter templates');
                    for (var j2 = 0; j2 < (templates||[]).length; j2++) { Home.addTemplateLink(div, templates[j2]); }
                }
                // Then add user projects (guard against undefined)
                try {
                    var projCount = (Array.isArray(data) && typeof data.length === 'number') ? data.length : 0;
                    for (var i = 0; i < projCount; i++) {
                        Home.addProjectLink(div, data[i]);
                    }
                } catch (_) {}
                let tlen = 0; let dlen = 0;
                try { tlen = (templates && typeof templates.length === 'number') ? templates.length : 0; } catch(_) {}
                try { dlen = (Array.isArray(data) && typeof data.length === 'number') ? data.length : 0; } catch(_) {}
                console.log(`✅ Rendered ${tlen} templates + ${dlen} user projects to HIDDEN staging`);

                // If empty on first pass after an index trip, kick a cache refresh once
                try {
                    if (!Home._refreshKick && (tlen === 0 && dlen === 0)) {
                        Home._refreshKick = true;
                        (async function(){
                            try {
                                if (window.dbModuleInitPromise) { await window.dbModuleInitPromise; }
                                if (window.dbModule && typeof window.dbModule.refreshCache === 'function') {
                                    await window.dbModule.refreshCache();
                                }
                            } catch(_) {}
                            try { Home.displayYourProjects(); } catch(_) {}
                        })();
                    }
                } catch(_) {}

                // Schedule debounced atomic swap (wait for events to settle)
                Home.scheduleAtomicSwap();
            };

            try {
                if (window.dbModule && window.dbModule.cacheReady && window.dbModule.projectCache) {
                    const templates = Array.from(window.dbModule.projectCache.values())
                        .filter(p => (p && (p.gallery === 'templates' || (p.metadata && p.metadata.gallery === 'templates'))))
                        .map(p => ({
                            id: p.id,
                            name: p.name,
                            thumbnail: (typeof p.thumbnail === 'string') ? p.thumbnail : JSON.stringify(p.thumbnail),
                            json: (typeof p.json === 'string') ? p.json : JSON.stringify(p.json)
                        }));
                    renderAfterTemplates(templates);
                } else {
                    // Render with empty templates and allow cache events to update later
                    renderAfterTemplates([]);
                }
            } catch (e) {
                console.warn('⚠️ [Home] Failed to load templates:', e && e.message);
                renderAfterTemplates([]);
            }
        } catch (e) {
            console.error('❌ displayProjects failed:', e && e.message);
        } finally {
            // Always unlock render flag (Claude review issue #2)
            _isRendering = false;
        }
    }

    /**
     * Schedule an atomic swap from hidden staging to visible DOM
     * Debounces to wait for all database events to settle
     * Issue #638: Only the final settled state becomes visible - NO flashing
     */
    static scheduleAtomicSwap() {
        // Clear any pending swap
        if (_renderDebounceTimer) {
            clearTimeout(_renderDebounceTimer);
            console.log('⏱️ Clearing previous swap timer - events still settling');
        }

        // Schedule new swap after settle period
        _renderDebounceTimer = setTimeout(() => {
            Home.atomicSwapToDom();
        }, RENDER_SETTLE_MS);

        console.log(`⏱️ Scheduled atomic swap in ${RENDER_SETTLE_MS}ms (waiting for events to settle)`);
    }

    /**
     * Perform ONE atomic swap from hidden staging to visible DOM
     * This is the ONLY moment the user sees ANY change
     * Issue #638: Single visible paint - no intermediate states
     */
    static atomicSwapToDom() {
        console.log('🔄 Executing ATOMIC SWAP from hidden to visible');

        const visibleContainer = gn('scrollarea');
        if (!visibleContainer) {
            console.warn('⚠️ Visible container not found, cannot swap');
            return;
        }

        if (!_hiddenStaging || !_hiddenStaging.firstChild) {
            console.warn('⚠️ Hidden staging empty, nothing to swap');
            return;
        }

        // Issue #638: Content diffing - skip swap if content identical (prevents flash)
        // Compare project IDs in order to detect if anything actually changed
        try {
            const visibleIds = Array.from(visibleContainer.children).map(c => c.id).join(',');
            const stagingIds = Array.from(_hiddenStaging.children).map(c => c.id).join(',');

            if (visibleIds === stagingIds && visibleIds.length > 0) {
                console.log('⏭️ Skipping swap - content unchanged (same projects in same order)');
                // Clean up staging since we're not using it
                try {
                    if (_hiddenStaging && _hiddenStaging.parentNode) {
                        _hiddenStaging.parentNode.removeChild(_hiddenStaging);
                        _hiddenStaging = null;
                    }
                } catch (_) {}
                _renderDebounceTimer = null;
                return;
            }
            console.log(`📊 Content changed - visible: ${visibleIds.split(',').length} items, staging: ${stagingIds.split(',').length} items`);
        } catch (e) {
            console.warn('⚠️ Content diffing failed, proceeding with swap:', e && e.message);
        }

        // INSTANT ATOMIC OPERATION: Replace ALL children in single synchronous operation
        // This is MUCH faster than removeChild loop + appendChild loop
        // Browser processes this as ONE DOM mutation = ONE paint = NO visible flash
        try {
            // Use DocumentFragment for instant atomic swap (single reflow/repaint)
            const fragment = document.createDocumentFragment();

            // Move ALL staging children to fragment (instant, no reflow)
            while (_hiddenStaging.firstChild) {
                fragment.appendChild(_hiddenStaging.firstChild);
            }

            // ATOMIC: Clear visible and insert fragment in ONE operation
            visibleContainer.textContent = '';  // Fastest clear (single operation)
            visibleContainer.appendChild(fragment);  // Single DOM insertion

            console.log('✅ INSTANT ATOMIC SWAP - zero visible delay');
        } catch (e) {
            console.error('❌ Atomic swap failed:', e && e.message);
        }

        // Clean up staging container to prevent memory leak (Claude review issue #1)
        try {
            if (_hiddenStaging && _hiddenStaging.parentNode) {
                _hiddenStaging.parentNode.removeChild(_hiddenStaging);
                _hiddenStaging = null;
                console.log('🧹 Cleaned up hidden staging container (memory leak prevention)');
            }
        } catch (e) {
            console.warn('⚠️ Failed to clean up staging container:', e && e.message);
        }

        // Finalize: restore scroll position
        try {
            setTimeout(function () { Lobby.busy = false; }, 1000);
            if (gn('wrapc')) { gn('wrapc').scrollTop = scrollvalue; }
            // Mark render complete and coalesce pending re-render if any
            _renderLastRun = Date.now();
            _renderInProgress = false;
            // After first successful render, freeze future auto-refreshes; allow one forced refresh once
            if (!initialRenderDone) {
                _freezeLobbyRefresh = true;
                _allowOneForcedRefresh = true;
            } else if (_freezeLobbyRefresh && _allowOneForcedRefresh) {
                // If this was the forced refresh, consume it
                _allowOneForcedRefresh = false;
            }
            if (_needsRerender) { _needsRerender = false; setTimeout(() => Home.displayYourProjects(), 50); }
            // Ensure gift import tile is present and correctly placed
            try {
                if (typeof window.installGiftTriggerTile === 'function') {
                    window.installGiftTriggerTile();
                }
            } catch (_) {}
        } catch (_) {}

        _lastRenderTime = Date.now();
        _renderDebounceTimer = null;
    }

    // Deterministic, sequential initial render to eliminate flakiness
    static async renderDeterministic () {
        if (initialRenderStarted) { return initialRenderPromise; }
        initialRenderStarted = true;

        // Helper to sleep
        const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

        initialRenderPromise = (async () => {
            try {
                // Ensure container exists and is empty
                var div = gn('scrollarea');
                if (!div) {
                    if (!frame) { frame = gn('htmlcontents'); }
                    if (frame) {
                        var inner = gn('inner') || newHTML('div', 'inner', frame);
                        div = newHTML('div', 'scrollarea', inner);
                        div.setAttribute('id', 'scrollarea');
                    }
                }
                if (!div) { return; }
                try { while (div.firstChild) div.removeChild(div.firstChild); } catch (_) {}

                // 1) New Project tile
                Home.emptyProjectThumbnail(div);
                // 2) Gift tile (immediately after new project)
                try { if (typeof window.installGiftTriggerTile === 'function') { window.installGiftTriggerTile(); } } catch (_) {}

                // Prepare DB (slow but deterministic): await init, writes, and refresh
                let db = null;
                try {
                    if (window.dbModuleInitPromise && typeof window.dbModuleInitPromise.then === 'function') {
                        db = await window.dbModuleInitPromise;
                    } else {
                        db = window.dbModule || null;
                    }
                } catch (_) {}
                try { if (db && typeof db.waitForWrites === 'function') { await db.waitForWrites(); } } catch (_) {}
                // Optionally seed defaults via DatabaseModule if available (guarded)
                try {
                    let defaultsSeeded = false;
                    try {
                        defaultsSeeded = (localStorage.getItem('sjr:defaults-seeded') === '1') || !!localStorage.getItem('scratchjr_file_starter_templates_loaded_v1');
                    } catch(_) {}
                    if (db && typeof db.ensureDefaultProjects === 'function' && !defaultsSeeded) {
                        const starterList = [
                            'starter-projects/dance.txt',
                            'starter-projects/animal-race.txt'
                        ];
                        await db.ensureDefaultProjects(starterList);
                        try { localStorage.setItem('sjr:defaults-seeded', '1'); } catch(_) {}
                    }
                } catch (_) {}
                try { if (db && typeof db.refreshCache === 'function') { await db.refreshCache(); } } catch (_) {}

                // 3) Starter templates
                let templates = [];
                try {
                    if (db && db.projectCache) {
                        templates = Array.from(db.projectCache.values()).filter(function(p){
                            return (p && ((p.gallery === 'templates') || (p.metadata && p.metadata.gallery === 'templates')));
                        }).sort(function(a,b){ return (a.ctime||0) - (b.ctime||0); })
                        .map(function(p){ return ({ id: p.id, name: p.name, thumbnail: (typeof p.thumbnail==='string'?p.thumbnail:JSON.stringify(p.thumbnail||{})), json: (typeof p.json==='string'?p.json:JSON.stringify(p.json||{})) }); });
                    } else { templates = []; }
                } catch (_) { templates = []; }
                try {
                    // De-duplicate templates by name to avoid duplicate displays
                    const seen = new Set();
                    for (var j = 0; j < templates.length; j++) {
                        try {
                            var tdata3 = IO.parseProjectData(templates[j]);
                            var nm = tdata3 && tdata3.name;
                            if (nm && !seen.has(nm)) { seen.add(nm); Home.addTemplateLink(div, templates[j]); }
                        } catch (_) { /* ignore */ }
                    }
                } catch (_) {}

                // 4) User projects
                let projects = [];
                try {
                    if (db && db.projectCache) {
                        // Version filter removed (Issue #681) - show all projects regardless of version
                        projects = Array.from(db.projectCache.values()).filter(function(p){
                            // Fix Issue #681: Treat undefined as 'NO' (not deleted)
                            var deleted = (p.metadata && p.metadata.deleted !== undefined)
                                ? p.metadata.deleted
                                : (p.deleted !== undefined ? p.deleted : 'NO');
                            var gallery = (p.metadata && p.metadata.gallery) ? p.metadata.gallery : (p.gallery || null);
                            return deleted === 'NO' && (gallery === null || gallery === undefined) && projectMatchesActiveProfile(p);
                        }).sort(function(a,b){
                            var am = (a.mtime != null ? parseInt(a.mtime, 10) : null);
                            var bm = (b.mtime != null ? parseInt(b.mtime, 10) : null);
                            if (isNaN(am)) am = (a.lastModified || a.createdAt || 0);
                            if (isNaN(bm)) bm = (b.lastModified || b.createdAt || 0);
                            return (bm||0) - (am||0);
                        }).map(function(p){ return ({ id: p.id, name: p.name, thumbnail: (typeof p.thumbnail==='string'?p.thumbnail:JSON.stringify(p.thumbnail||{})), isgift: (p.metadata && p.metadata.isgift) || p.isgift || '0', json: (typeof p.json==='string'?p.json:JSON.stringify(p.json||{})) }); });
                    } else { projects = []; }
                } catch (_) { projects = []; }
                try { for (var i = 0; i < projects.length; i++) { Home.addProjectLink(div, projects[i]); } } catch (_) {}

                // Finalize
                try { if (gn('wrapc')) { gn('wrapc').scrollTop = scrollvalue || 0; } } catch (_) {}
                try { Lobby.busy = false; } catch(_) {}
                // Ensure tile position one last time
                try { if (typeof window.installGiftTriggerTile === 'function') { window.installGiftTriggerTile(); } } catch (_) {}

            } catch (e) {
                console.warn('⚠️ Home.renderDeterministic failed:', e && e.message || e);
            } finally {
                initialRenderDone = true;

                // Enable suppression to prevent redundant event-driven renders
                suppressEventDrivenRenders = true;
                console.log(`🛡️ Suppressing event-driven renders for ${RENDER_SUPPRESSION_MS}ms to prevent double-flash`);

                // Auto-lift suppression after 1 second
                setTimeout(() => {
                    suppressEventDrivenRenders = false;
                    console.log('✅ Event-driven renders re-enabled');
                }, RENDER_SUPPRESSION_MS);
            }
        })();

        return initialRenderPromise;
    }

    static addTemplateLink (parent, aa) {
        var data = IO.parseProjectData(aa);
        var id = data.id;
        var th = data.thumbnail;
        if (!th) {
            return;
        }
        var thumb = (typeof th === 'string') ? JSON.parse(th) : th;
        var pc = thumb.pagecount ? thumb.pagecount : 1;
        var tb = newHTML('div', 'projectthumb template', parent);
        tb.setAttribute('id', id);
        tb.type = 'templatethumb';  // Mark as template
        tb.thumb = thumb.md5;
        var mt = newHTML('div', 'aproject p' + pc, tb);
        Home.insertThumbnail(mt, 192, 144, thumb, data);
        var label = newHTML('div', 'projecttitle', tb);
        var txt = newHTML('h4', undefined, label);
        txt.textContent = data.name;

        // Templates don't have delete button (no closex element)
        // They're read-only and create copies when clicked
    }

    static addProjectLink (parent, aa) {
        var data = IO.parseProjectData(aa);
        var id = data.id;
        var th = data.thumbnail;
        // Gifts get special visual treatment (white box + bow/ribbons), ignore thumbnail
        var isGift = (data.isgift != '0');
        var thumb = (!isGift && th) ? ((typeof th === 'string') ? JSON.parse(th) : th) : { pagecount: 1 };
        var pc = thumb && thumb.pagecount ? thumb.pagecount : 1;
        var tb = newHTML('div', 'projectthumb', parent);
        tb.setAttribute('id', id);
        tb.type = 'projectthumb';
        // Mark seeded starter projects for copy-on-open
        if (data.starterflag && (data.starterflag === '1' || data.starterflag === 1)) {
            tb.setAttribute('data-starter', '1');
        }
        if (!isGift) { tb.thumb = thumb.md5; }
        // For gifts, use the standard white paper background (same as new project)
        // and overlay a white mask image to avoid the plus icon
        var mt = newHTML('div', isGift ? 'aproject empty' : ('aproject p' + pc), tb);
        if (isGift) {
            // For gifts, use a white placeholder image sized exactly like thumbnails
            try {
                tb.className += ' giftwrap';
                var img = newHTML('img', undefined, mt);
                // Use the project mask image which renders a plain white interior with the correct
                // irregular paper border, effectively hiding the plus icon from newproject.png
                img.src = 'assets/lobby/projectmask.png';
            } catch (_) {}
        } else {
            Home.insertThumbnail(mt, 192, 144, thumb, data);
        }
        var label = newHTML('div', 'projecttitle', tb);
        var txt = newHTML('h4', undefined, label);
        txt.textContent = data.name;

        // Legacy CSS-based gift wrap: add overlays and toggle visibility
        var bow = newHTML('div', 'share', tb);
        var ribbonHorizontal = newHTML('div', 'ribbonHorizontal', tb);
        var ribbonVertical = newHTML('div', 'ribbonVertical', tb);
        if (isGift) {
            bow.style.visibility = 'visible';
            ribbonHorizontal.style.visibility = 'visible';
            ribbonVertical.style.visibility = 'visible';
            // Ensure overlays never block interactions (e.g., long-press delete closex)
            try {
                bow.style.pointerEvents = 'none';
                ribbonHorizontal.style.pointerEvents = 'none';
                ribbonVertical.style.pointerEvents = 'none';
            } catch (_) {}
        }

        var cx = newHTML('div', 'closex', tb);
        try { cx.style.zIndex = '6'; } catch (_) {}
    }

    static insertThumbnail (p, w, h, data, projectData) {
        var md5 = data.md5;
        var img = newHTML('img', undefined, p);
        // Immediate safe default: background image so we don't flash a broken/blank thumbnail
        try {
            let fallbackBkg = 'backgrounds/Farm.svg';
            if (projectData && projectData.json) {
                let j = projectData.json;
                if (typeof j === 'string') { try { j = JSON.parse(j); } catch (_) { j = null; } }
                if (j && typeof j === 'object') {
                    const pageKey = j.currentPage || (Array.isArray(j.pages) && j.pages[0]) || 'page 1';
                    if (j[pageKey] && j[pageKey].md5) { fallbackBkg = j[pageKey].md5; }
                }
            }
            IO.getAsset(fallbackBkg, function(url){ if (url && !img.src) { img.src = url; } });
        } catch (_) {}
        if (md5) { IO.getAsset(md5, drawMe); }
        img.ondragstart = function () {
            return false;
        };
        function drawMe (url) {
            if (url && typeof url === 'string' && url.length > 0) {
                img.src = url;
                return;
            }
            // Fallback: attempt to render from project background if thumbnail missing
            try {
                if (projectData && projectData.json) {
                    let j = projectData.json;
                    if (typeof j === 'string') { try { j = JSON.parse(j); } catch (_) { j = null; } }
                    if (j && typeof j === 'object') {
                        const pageKey = j.currentPage || (Array.isArray(j.pages) && j.pages[0]) || 'page 1';
                        const bkg = (j[pageKey] && j[pageKey].md5) ? j[pageKey].md5 : 'backgrounds/Farm.svg';
                        return IO.getAsset(bkg, function(fallbackUrl){ img.src = fallbackUrl || ''; });
                    }
                }
            } catch (_) {}
            // Last resort: leave blank (no crash)
            img.src = '';
        }
    }
}

class Events {
    static getTargetPoint (e) {
        if (isTablet) {
            if (e.touches && (e.touches.length > 0)) {
                return {
                    x: e.touches[0].pageX,
                    y: e.touches[0].pageY
                };
            } else if (e.changedTouches) {
                return {
                    x: e.changedTouches[0].pageX,
                    y: e.changedTouches[0].pageY
                };
            }
        }
        return {
            x: e.clientX,
            y: e.clientY
        };
    }
}

// Expose Home for direct calls from page scripts (post-editor refresh flows)
try { if (typeof window !== 'undefined') { window.Home = Home; } } catch (_) {}
